import * as React from "react";
import { useForm, FormProvider } from "react-hook-form";
import { toast } from "react-toastify";
import { useToggle } from "react-use";
import { Dialog, DialogContent, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  useGetSnapshotEventMailsQuery,
  useCreateSnapshotEventMailMutation,
  useUpdateSnapshotEventMailMutation,
  useDeleteSnapshotEventMailMutation,
} from "@/modules/snapshots/businessReports.api";
import { SnapshotEventMail } from "@/modules/snapshots/businessReports.types";
import { NotesSidebar } from "./components/NotesSidebar";
import { NoteEditor } from "./components/NoteEditor";
import { NoteFormData, toFormData, EMPTY_FORM } from "./types";

interface Props {
  applyNoteFn?: (note: string) => void;
  open: boolean;
  onClose: () => void;
  hideFooter?: boolean;
}

const EMPTY_NOTES: SnapshotEventMail[] = [];
const getDefaultNote = (notes: SnapshotEventMail[]) => notes.find(n => n.is_default) ?? notes[0];

export const SnapshotEventMailModal = ({ open, onClose, applyNoteFn, hideFooter }: Props) => {
  const { data: notes = EMPTY_NOTES, isLoading } = useGetSnapshotEventMailsQuery(undefined, { skip: !open });
  const [createMail] = useCreateSnapshotEventMailMutation();
  const [updateMail] = useUpdateSnapshotEventMailMutation();
  const [deleteMail] = useDeleteSnapshotEventMailMutation();

  const [selectedNoteId, setSelectedNoteId] = React.useState<string | null>(null);
  const [isCreatingNew, toggleIsCreatingNew] = useToggle(false);

  const methods = useForm<NoteFormData>({ defaultValues: EMPTY_FORM });

  const selectedNote = React.useMemo(() => notes.find(n => n.id === selectedNoteId), [notes, selectedNoteId]);

  // react-hook-form's reset and the useToggle dispatcher are stable, so these effects still only
  // react to the dialog opening and to note/selection changes.
  const { reset } = methods;

  const handleNewNote = React.useCallback(() => {
    toggleIsCreatingNew(true);
    setSelectedNoteId(null);
    reset(EMPTY_FORM);
  }, [reset, toggleIsCreatingNew]);

  React.useEffect(() => {
    if (open) {
      setSelectedNoteId(null);
      toggleIsCreatingNew(false);
      reset(EMPTY_FORM);
    }
  }, [open, reset, toggleIsCreatingNew]);

  React.useEffect(() => {
    if (notes.length > 0 && !selectedNoteId && !isCreatingNew) {
      const note = getDefaultNote(notes);
      setSelectedNoteId(note.id);
      reset(toFormData(note));
    }
    if (notes.length === 0 && !isCreatingNew) {
      handleNewNote();
    }
  }, [notes, selectedNoteId, isCreatingNew, reset, handleNewNote]);

  const handleSelectNote = (note: SnapshotEventMail) => {
    toggleIsCreatingNew(false);
    setSelectedNoteId(note.id);
    methods.reset(toFormData(note));
  };

  const handleSave = async () => {
    const data = methods.getValues();

    if (!data.title.trim()) {
      toast.error("Title is required");
      return;
    }

    if (isCreatingNew) {
      await createMail(data).unwrap();
      toast.success("Note created successfully");
      toggleIsCreatingNew(false);
    } else if (selectedNoteId) {
      await updateMail({ id: selectedNoteId, data }).unwrap();
      toast.success("Note updated successfully");
    }
  };

  const handleDelete = async () => {
    if (!selectedNoteId) return;

    await deleteMail(selectedNoteId).unwrap();
    toast.success("Note deleted successfully");
    setSelectedNoteId(null);
    methods.reset(EMPTY_FORM);
  };

  const handleCancel = () => {
    if (hideFooter) {
      onClose();
      return;
    }
    if (isCreatingNew) {
      toggleIsCreatingNew(false);
      if (notes.length > 0) {
        handleSelectNote(getDefaultNote(notes));
      }
    } else if (selectedNote) {
      methods.reset(toFormData(selectedNote));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[95vh] w-[900px] max-w-[95vw] overflow-hidden rounded-lg bg-white p-0">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <DialogTitle className="text-2xl font-bold">Email notes</DialogTitle>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <NotesSidebar
            notes={notes}
            isLoading={isLoading}
            selectedNoteId={selectedNoteId}
            isCreatingNew={isCreatingNew}
            onSelectNote={handleSelectNote}
            onNewNote={handleNewNote}
          />

          <FormProvider {...methods}>
            <NoteEditor
              isCreatingNew={isCreatingNew}
              hasSelection={!!selectedNoteId}
              onSave={handleSave}
              onDelete={handleDelete}
              onCancel={handleCancel}
            />
          </FormProvider>
        </div>

        {!hideFooter && (
          <DialogFooter className="flex justify-end gap-3 border-t border-gray-200 bg-ivory-mist-200 px-6 py-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            {applyNoteFn && (
              <Button
                type="button"
                onClick={() => {
                  applyNoteFn(methods.getValues("message"));
                  onClose();
                }}
                disabled={!selectedNoteId && !isCreatingNew}
              >
                Use this note
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
